import { useCallback, useEffect, useState } from 'react';
import { filter } from 'lodash';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Grid,
  CardContent,
  Typography,
  CardActions,
  Button,
  Divider,
  Paper,
  TableHead
} from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';

import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import * as yup from 'yup';
import HistoryIcon from '@mui/icons-material/History';
import { yupResolver } from '@hookform/resolvers/yup';
import { fDateTime } from '../../utils/formatTime';
import CustomInput from '../../components/CustomInput';
import CustomDialog from '../../components/CustomDialogue';

// components
import Page from '../../components/Page';
import { ReportApi } from '../../utils/Apis';
import { NOT_AVAILABLE } from '../Global/Constants';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import ReportInvoiceToolbar from './ReportToolbar';
import ReportService from '../services/ReportService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import { getHistoryReportList, getReportsList } from '../../store/Slice/Report/ReportSlice';
import Label from '../../components/Label';
import {
  downloadApiData,
  getBankDropdownData,
  getLoanTypeData
} from '../../store/Slice/Global/GlobalSlice';
import useReportSelector from '../../store/Slice/Report/ReportSelector';
import { setIsLoading } from '../../store/Slice/Loader/LoaderSlice';
import { normalizeValue, truncateText } from '../../utils/CommonFunction';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';

const { DownloadMisReports } = ReportService;

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export default function Report() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloadPopup, setDownloadPopup] = useState(false);

  const [HistoryPopup, setHistoryPopup] = useState(false);
  const [filterParameter, setFilterParameter] = useState({
    loanTypeId: null,
    fileLoginedBankId: null,
    fileLoginedBranchId: null,
    dsaCode: null,
    statusId: null
  });
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const debouncedSearch = useDebounce(filterName, 1000);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { ReportListData, ReportHistoryData } = useReportSelector();
  console.log('ReportHistoryData', ReportHistoryData);

  // Handle download button click
  const handleDownloadClick = (report) => {
    console.log('selected report in handledownload', report);
    setSelectedReport(report);
    downloadReset();
    setDownloadPopup(true);
  };

  const handleHistoryClick = async (report) => {
    console.log('History clicked for report:', report?.reportCode);

    const result = await dispatch(getHistoryReportList(report?.reportCode));
    console.log('history result', result);

    setSelectedReport(report);
    setHistoryPopup(true);
    // Implement history functionality here, e.g., navigate to history page or open history dialog
  };

  const handleCloseHistoryDialog = () => {
    setHistoryPopup(false);
    setSelectedReport(null);
  };

  // Close download dialog
  const handleCloseDownloadDialog = () => {
    setDownloadPopup(false);
    setSelectedReport(null);
    downloadReset();
  };

  const {
    handleSubmit: handleDownloadSubmit,
    control: downloadControl,
    reset: downloadReset,
    formState: { errors: downloadErrors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      fromDate: '',
      toDate: ''
    }
  });

  // Handle download form submission
  const onDownloadSubmit = useCallback(
    async (data) => {
      setIsDownloading(true);
      console.log('selected data', selectedReport?.reportCode);
      const fromDateString = format(new Date(data.fromDate), 'yyyy-MM-dd');
      const toDateString = format(new Date(data.toDate), 'yyyy-MM-dd');
      try {
        const downloadCredentials = {
          companyId: LoginData?.companyId,
          userId: LoginData?.userId,
          fromDate: fromDateString,
          toDate: toDateString,
          loanTypeId: filterParameter?.loanTypeId || null,
          city: null
        };

        // Call the download API from leadsService
        console.log(
          'downloadendpoint',
          `${ReportApi?.reportDownload}/${selectedReport?.reportCode}`
        );
        const result = await dispatch(
          downloadApiData(
            `${ReportApi?.reportDownload}/${selectedReport?.reportCode}`,
            downloadCredentials
          )
        );

        if (result?.success && result?.data) {
          const fileName = `Report_${selectedReport?.reportCode}_Download_${format(
            new Date(),
            'dd-MM-yyyy_HH-mm-ss'
          )}.xlsx`;

          const url = window.URL.createObjectURL(result.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          console.error('Download failed:', result.error);
        }
      } catch (error) {
        console.error('Export error:', error);
      } finally {
        setIsDownloading(false);
      }
    },
    [selectedReport, LoginData, filterParameter, dispatch]
  );

  const getReportApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      reportFilterModel: {
        fileLoginedBankId: filterParameter?.fileLoginedBankId || null,
        fileLoginedBranchId: filterParameter?.fileLoginedBranchId || null,
        dsaCode: filterParameter?.dsaCode || null,
        loanTypeId: filterParameter?.loanTypeId || null,
        statusId: filterParameter?.statusId
          ? filterParameter?.statusId === 'MATCHED'
            ? true
            : false
          : null,
        toLeadDate: null
      },
      searchAndPaginationModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      }
    };
    dispatch(getReportsList(credentials));
  }, [LoginData, filterName, filterParameter, debouncedSearch]);

  useEffect(() => {
    getReportApi();
  }, [getReportApi]);

  return (
    <Page title="Partner Management System || Report">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Report Cards" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Scrollbar>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {ReportListData?.length > 0 ? (
                ReportListData?.map((report) => (
                  <Grid item xs={12} sm={6} md={4} lg={4} key={report?.id}>
                    <Card
                      sx={{
                        height: '100%',
                        minHeight: '160px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 1,
                        '&:hover': {
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h6" component="div" noWrap>
                          {report?.reportName || NOT_AVAILABLE}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }} wrap>
                          {report?.reportDescription || NOT_AVAILABLE}
                        </Typography>
                      </CardContent>
                      <Divider component="li" sx={{ p: 0 }} />
                      <CardActions sx={{ pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={<DownloadForOfflineIcon />}
                          onClick={() => handleDownloadClick(report)}
                        >
                          Download
                        </Button>

                        <Button
                          size="small"
                          startIcon={<HistoryIcon />}
                          onClick={() => handleHistoryClick(report)}
                        >
                          History
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <SearchNotFound searchQuery={debouncedSearch} />
                </Grid>
              )}
            </Grid>
          </Scrollbar>
        </Box>

        <CustomDialog
          showCloseIcon
          open={downloadPopup}
          maxWidth="md"
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseDownloadDialog();
            }
          }}
          title={`Download ${selectedReport?.reportName || 'Report'}`}
          content={
            <Box mb={3}>
              <form id="DownloadReportForm" onSubmit={handleDownloadSubmit(onDownloadSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <CustomInput
                      name="fromDate"
                      label="From Date"
                      type="date"
                      control={downloadControl}
                      errors={downloadErrors}
                      isRequired
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomInput
                      name="toDate"
                      label="To Date"
                      type="date"
                      control={downloadControl}
                      errors={downloadErrors}
                      isRequired
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                </Grid>
              </form>
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={handleCloseDownloadDialog}
                disabled={isDownloading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="DownloadReportForm"
                variant="contained"
                className="mainBtn"
                color="primary"
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Apply'}
              </Button>
            </Box>
          }
        />

        <CustomDialog
          showCloseIcon
          open={HistoryPopup}
          maxWidth="lg"
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseHistoryDialog();
            }
          }}
          title={`History of ${selectedReport?.reportName || 'Report'} `}
          content={
            <Box mb={3}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>Sr.No.</b>
                      </TableCell>
                      <TableCell>
                        <b>Downloaded At</b>
                      </TableCell>
                      <TableCell>
                        <b>File Name</b>
                      </TableCell>
                      <TableCell>
                        <b>User Name</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {ReportHistoryData?.map((row, index) => (
                      <TableRow key={row.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{fDateTime(row?.downloadedAt)}</TableCell>
                        <TableCell>{row?.fileName}</TableCell>
                        <TableCell>{row?.userName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={handleCloseHistoryDialog}
                disabled={isDownloading}
              >
                Cancel
              </Button>
            </Box>
          }
        />
      </Container>
    </Page>
  );
}
